import { inject, Injectable, OnDestroy, Pipe, PipeTransform, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';

import { Nullable } from '../helpers/types';


/*** Translation list initializer function type. */
export type TranslationListInitializerFn<I18nData> = () => Observable<I18nData[]>;

/** Translation list filter by lang function type. */
export type TranslationListFilterByLangFn<I18nData> = (lg: string, translation: I18nData) => boolean;

/** Translation list filter by code function type. */
export type TranslationListFilterByCodeFn<I18nData> = (code: string, translation: I18nData) => boolean;


/** Translation list filter by country and lang function type. */
export type TranslationListFilterByCountryByLangFn<I18nData> = (countryCode: string, lg: string,translation: I18nData) => boolean;

/** Get translation text value function. */
export type GetTranslationValueFn<I18nData> = (translation: I18nData) => Nullable<string>;

/** Translation sorter (translation priority). */
export type TranslationSorterFn<I18nData> = (a: I18nData, b: I18nData) => number;


/** Translate options. */
export interface TranslateOptions {
    /**
     * If false, return code if no translation found (default behavior).
     * If true, return empty string if no translation found.
     */
    returnEmptyIfNotFound?: boolean;
}


/**
 * Translate service.
 * TODO Pass vars (inside {{ }}) to translation.
 */
@Injectable({
    providedIn: 'root'
})
export class TranslateService<I18nData> implements OnDestroy {

    /** Current lang. */
    private _currentLangSubject!: BehaviorSubject<string>;

    /** Current lang observable. */
    currentLang$!: Observable<string>;

    /** Signal version of currentLang$. */
    private _currentLangSgl!: WritableSignal<string>;

    /** Translations list getter.  */
    private _translationsListInitializerFn!: TranslationListInitializerFn<I18nData>;

    /** Filter translations by lang. */
    private _translationListFilterByLangFn!: TranslationListFilterByLangFn<I18nData>;

    /** Filter translations by country and lang. */
    private _translationListFilterByCountryByLangFn!: TranslationListFilterByCountryByLangFn<I18nData>;

    /** Filter translations by code. */
    private _translationListFilterByCodeFn!: TranslationListFilterByCodeFn<I18nData>;

    /** Get translation value function. */
    private _getTranslationValueFn!: GetTranslationValueFn<I18nData>;

    /** Translation sorter (translation priority). */
    private _translationSorterFn: Nullable<TranslationSorterFn<I18nData>> = null;

    /** Translations. */
    private _translations$!: Observable<I18nData[]>;

    /** Signal version of _translations$ */
    private _translationsSgl: WritableSignal<I18nData[]> = signal([]);

    /** Unsub. */
    private _unsub$: Subject<void> = new Subject();

    /** Init translate service. */
    async init(
        configs: {
            defaultLang: Promise<string>,
            translationsListInitializerFn: TranslationListInitializerFn<I18nData>,
            translationListFilterByLangFn: TranslationListFilterByLangFn<I18nData>,
            translationListFilterByCodeFn: TranslationListFilterByCodeFn<I18nData>,
            translationListFilterByCountryByLangFn: TranslationListFilterByCountryByLangFn<I18nData>,
            getTranslationValueFn: GetTranslationValueFn<I18nData>,
            sorterFn?: TranslationSorterFn<I18nData>
        }
    ): Promise<boolean> {
        this._currentLangSgl = signal(await configs.defaultLang);
        this._currentLangSubject = new BehaviorSubject(this._currentLangSgl());
        this.currentLang$ = this._currentLangSubject.asObservable();

        this._translationsListInitializerFn = configs.translationsListInitializerFn;
        this._translationListFilterByLangFn = configs.translationListFilterByLangFn;
        this._translationListFilterByCodeFn = configs.translationListFilterByCodeFn;
        this._translationListFilterByCountryByLangFn = configs.translationListFilterByCountryByLangFn;
        this._getTranslationValueFn = configs.getTranslationValueFn;
        this._translationSorterFn = configs.sorterFn;

        this.currentLang$.pipe(takeUntil(this._unsub$)).subscribe({
            next: (lg: string) => this._currentLangSgl.set(lg)
        });

        this._translations$ = this.currentLang$.pipe(
            switchMap(
                (lg: string) => this._translationsListInitializerFn().pipe(
                    map(
                        (translations: I18nData[]) => {
                            const translationsList = translations.filter(
                                (value: I18nData) => this._translationListFilterByLangFn(lg, value)
                            );
                            if (!this._translationSorterFn) {
                                return translationsList;
                            }
                            return translationsList.sort(this._translationSorterFn);
                        }
                    )
                )
            )
        );

        this._translations$.pipe(takeUntil(this._unsub$)).subscribe({
            next: (translations: I18nData[]) => this._translationsSgl.set(translations)
        });

        return true;
    }

    /** OnDestroy. */
    ngOnDestroy(): void {
        this._unsub$.next();
        this._unsub$.complete();
    }

    /** Get text from translations list. */
    private _getTranslation(translations: I18nData[], code: string, options?: TranslateOptions): string {
        const i18n: Nullable<I18nData> = translations.find(
            (value: I18nData) => this._translationListFilterByCodeFn(code, value)
        );
        const translationValue: Nullable<string> = i18n ? this._getTranslationValueFn(i18n) : null;
        if (translationValue) {
            return translationValue;
        }
        return !!options?.returnEmptyIfNotFound ? '' : code;
    }
        /** Get text from translations list by country and lang. */
        private _getTranslationByCountryByLang(translations: I18nData[], countryCode: string, lang: string, code: string, options?: TranslateOptions): string {
            const i18n: Nullable<I18nData> = translations.find(
                (value: I18nData) => this._translationListFilterByCountryByLangFn(countryCode, lang, value) && this._translationListFilterByCodeFn(code, value)
            );
            const translationValue: Nullable<string> = i18n ? this._getTranslationValueFn(i18n) : null;
            if (translationValue) {
                return translationValue;
            }
            return !!options?.returnEmptyIfNotFound ? '' : code;
        }

    /** Get translated text (observable). */
    getText$(code: string, options?: TranslateOptions): Observable<string> {
        return this._translations$.pipe(
            map(translations => this._getTranslation(translations, code, options))
        );
    }

        /** Get translated text by country and lang (observable). */
        getTextByCountryByLang$(countryCode: string, lang: string, code: string, options?: TranslateOptions): Observable<string> {
            return this._translations$.pipe(
                map(translations => this._getTranslationByCountryByLang(translations, countryCode, lang, code, options))
            );
        }

    /** Get translated text (instant text). */
    getText(code: string, options?: TranslateOptions): string {
        return this._getTranslation(this._translationsSgl(), code, options);
    }

        /** Get translated text by country and lang (instant text). */
        getTextByCountryByLang(countryCode: string, lang: string, code: string, options?: TranslateOptions): string {
            return this._getTranslationByCountryByLang(this._translationsSgl(), countryCode, lang, code, options);
        }

    /** (Set) Set lang. */
    set lang(value: string) {
        this._currentLangSubject.next(value.toLowerCase());
    }

    /** (Get) Get current lang. */
    get lang(): string {
        return this._currentLangSgl();
    }

}


/** Translate text. */
@Pipe({
    name: 'translate$',
    standalone: true
})
export class TranslatePipe implements PipeTransform {

    /** Translate service. */
    private readonly _translateService: TranslateService<any> = inject(TranslateService<any>);

    /** Translate text from database. */
    transform(code: string, options?: TranslateOptions): Observable<string> {
        return this._translateService.getText$(code, options);
    }
    }

    /** Translate text by country and lang. */
    @Pipe({
        name: 'translateCountryLang$',
        standalone: true
    })
    export class TranslateCountryLangPipe implements PipeTransform {
        private readonly _translateService: TranslateService<any> = inject(TranslateService<any>);

        transform(countryCode: string, lang: string, code: string, options?: TranslateOptions): Observable<string> {
            return this._translateService.getTextByCountryByLang$(countryCode, lang, code, options);
        }
}
